import React, { useState, useEffect } from 'react';
import { User, Bell, CreditCard, Award, Shield, Save, CheckCircle, Star, Calendar, TrendingUp, SettingsIcon, Camera, Mail, Phone } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useUserData } from '@/hooks/useUserData';
import { supabase } from '@/integrations/supabase/client';

interface UserProfile {
  name: string;
  email: string;
  phone: string;
  company: string;
  role: string;
  bio: string;
  avatar: string;
}

interface Achievement {
  id: number;
  title: string;
  description: string;
  icon: string;
  earned: boolean;
  earnedDate: string | null;
  progress?: number;
  maxProgress?: number;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { profile: userProfile, achievements: userAchievements, loading: dataLoading } = useUserData();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);

  // Initialize profile from real user data
  const [profile, setProfile] = useState<UserProfile>({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    bio: '',
    avatar: '👤'
  });

  // Update profile when user data loads
  useEffect(() => {
    if (userProfile && user) {
      setProfile({
        name: userProfile.full_name || user.user_metadata?.full_name || '',
        email: user.email || '',
        phone: userProfile.phone || '',
        company: userProfile.company || '',
        role: userProfile.role || '',
        bio: userProfile.bio || '',
        avatar: userProfile.avatar_url || '👤'
      });
    }
  }, [userProfile, user]);
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState({
    emailUpdates: true,
    pushNotifications: true,
    weeklyReports: true,
    communityMessages: false,
    achievementAlerts: true,
    taskReminders: true
  });

  const [subscription] = useState({
    plan: userProfile?.subscription_status === 'pro' ? 'Pro' : userProfile?.subscription_status === 'yearly' ? 'Yearly' : 'Free',
    status: 'Active',
    nextBilling: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    amount: userProfile?.subscription_status === 'pro' ? '$29/month' : userProfile?.subscription_status === 'yearly' ? '$290/year' : 'Free'
  });

  // Convert user achievements to display format with fallback achievements
  const defaultAchievements = [
    { id: 1, title: 'First Steps', description: 'Complete your first task', icon: '🎯', earned: false, progress: 0, maxProgress: 1 },
    { id: 2, title: 'Week Warrior', description: 'Complete 7 days in a row', icon: '🔥', earned: false, progress: 0, maxProgress: 7 },
    { id: 3, title: 'Idea Validator', description: 'Complete idea validation', icon: '💡', earned: false, progress: 0, maxProgress: 1 },
    { id: 4, title: 'Revenue Rookie', description: 'Track your first revenue', icon: '💰', earned: false, progress: 0, maxProgress: 1 },
    { id: 5, title: 'Community Builder', description: 'Send 10 pod messages', icon: '👥', earned: false, progress: 0, maxProgress: 10 },
    { id: 6, title: 'Sprint Finisher', description: 'Complete 30-day sprint', icon: '🏆', earned: false, progress: 0, maxProgress: 30 }
  ];

  const achievements = userAchievements.length > 0 
    ? userAchievements.map(ach => ({
        id: parseInt(ach.id),
        title: ach.badge_name,
        description: ach.description,
        icon: ach.icon || '🏆',
        earned: true,
        earnedDate: new Date(ach.earned_at).toLocaleDateString(),
        progress: 1,
        maxProgress: 1
      }))
    : defaultAchievements;

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
    { id: 'achievements', label: 'Achievements', icon: Award },
    { id: 'privacy', label: 'Privacy', icon: Shield }
  ];

  const handleProfileUpdate = (field: keyof UserProfile, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleNotificationToggle = (setting: keyof typeof notifications) => {
    setNotifications(prev => ({ ...prev, [setting]: !prev[setting] }));
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: profile.name,
          phone: profile.phone,
          company: profile.company,
          role: profile.role,
          bio: profile.bio,
          avatar_url: profile.avatar,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;
      
      // Show success confirmation popup - SECURE (no innerHTML XSS vulnerability)
      const confirmDiv = document.createElement('div');
      confirmDiv.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
      
      // Create elements securely without innerHTML
      const modal = document.createElement('div');
      modal.className = 'bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl';
      
      const center = document.createElement('div');
      center.className = 'text-center';
      
      const iconDiv = document.createElement('div');
      iconDiv.className = 'w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4';
      
      // Create SVG element securely without innerHTML
      const svg = document.createElement('svg');
      svg.className = 'w-8 h-8 text-green-500';
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('viewBox', '0 0 24 24');
      
      const path = document.createElement('path');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      path.setAttribute('stroke-width', '2');
      path.setAttribute('d', 'M5 13l4 4L19 7');
      
      svg.appendChild(path);
      iconDiv.appendChild(svg);
      
      const title = document.createElement('h3');
      title.className = 'text-lg font-semibold text-gray-900 mb-2';
      title.textContent = 'Profile Updated!';
      
      const message = document.createElement('p');
      message.className = 'text-gray-600 mb-6';
      message.textContent = 'Your profile information has been saved successfully.';
      
      const button = document.createElement('button');
      button.className = 'bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors';
      button.textContent = 'Continue';
      button.onclick = () => confirmDiv.remove();
      
      center.appendChild(iconDiv);
      center.appendChild(title);
      center.appendChild(message);
      center.appendChild(button);
      modal.appendChild(center);
      confirmDiv.appendChild(modal);
      document.body.appendChild(confirmDiv);
      
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="text-3xl font-bold text-startup-navy mb-2 flex items-center">
            <SettingsIcon className="w-8 h-8 mr-3 text-startup-teal" />
            Settings ⚙️
          </h1>
          <p className="text-startup-gray">
            Manage your profile, preferences, and account settings
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="space-y-2">
            <div className="card-startup animate-fade-in">
              <nav className="space-y-1">
                {tabs.map((tab, index) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 text-left animate-fade-in ${
                      activeTab === tab.id
                        ? 'bg-startup-teal text-white shadow-lg'
                        : 'text-startup-gray hover:text-startup-navy hover:bg-startup-slate'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Profile Settings */}
            {activeTab === 'profile' && (
              <div className="card-startup animate-fade-in">
                <h2 className="text-2xl font-semibold text-startup-navy mb-6 flex items-center">
                  <User className="w-6 h-6 mr-3 text-startup-teal" />
                  Profile Information
                </h2>

                <div className="space-y-6">
                  {/* Avatar Section */}
                  <div className="flex items-center space-x-6">
                    <div className="w-24 h-24 bg-startup-teal/10 rounded-full flex items-center justify-center text-4xl">
                      {profile.avatar}
                    </div>
                    <div>
                      <button className="btn-startup-outline flex items-center space-x-2">
                        <Camera className="w-4 h-4" />
                        <span>Change Avatar</span>
                      </button>
                      <p className="text-startup-gray text-sm mt-2">
                        Upload a photo or choose an emoji
                      </p>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-startup-navy font-medium mb-2">Full Name</label>
                      <input
                        type="text"
                        value={profile.name}
                        onChange={(e) => handleProfileUpdate('name', e.target.value)}
                        className="w-full p-3 border-2 border-startup-teal/20 rounded-xl focus:border-startup-teal focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-startup-navy font-medium mb-2">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-startup-gray" />
                        <input
                          type="email"
                          value={profile.email}
                          onChange={(e) => handleProfileUpdate('email', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border-2 border-startup-teal/20 rounded-xl focus:border-startup-teal focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-startup-navy font-medium mb-2">Phone</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-startup-gray" />
                        <input
                          type="tel"
                          value={profile.phone}
                          onChange={(e) => handleProfileUpdate('phone', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border-2 border-startup-teal/20 rounded-xl focus:border-startup-teal focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-startup-navy font-medium mb-2">Company</label>
                      <input
                        type="text"
                        value={profile.company}
                        onChange={(e) => handleProfileUpdate('company', e.target.value)}
                        className="w-full p-3 border-2 border-startup-teal/20 rounded-xl focus:border-startup-teal focus:outline-none"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-startup-navy font-medium mb-2">Role</label>
                      <input
                        type="text"
                        value={profile.role}
                        onChange={(e) => handleProfileUpdate('role', e.target.value)}
                        className="w-full p-3 border-2 border-startup-teal/20 rounded-xl focus:border-startup-teal focus:outline-none"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-startup-navy font-medium mb-2">Bio</label>
                      <textarea
                        value={profile.bio}
                        onChange={(e) => handleProfileUpdate('bio', e.target.value)}
                        className="w-full p-3 border-2 border-startup-teal/20 rounded-xl focus:border-startup-teal focus:outline-none resize-none h-24"
                        placeholder="Tell us about yourself and your startup journey..."
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="btn-startup-primary flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Notifications */}
            {activeTab === 'notifications' && (
              <div className="card-startup animate-fade-in">
                <h2 className="text-2xl font-semibold text-startup-navy mb-6 flex items-center">
                  <Bell className="w-6 h-6 mr-3 text-startup-teal" />
                  Notification Preferences
                </h2>

                <div className="space-y-6">
                  {[
                    { key: 'emailUpdates', label: 'Email Updates', description: 'Receive important updates via email' },
                    { key: 'pushNotifications', label: 'Push Notifications', description: 'Get real-time notifications in your browser' },
                    { key: 'weeklyReports', label: 'Weekly Reports', description: 'Weekly summary of your progress and achievements' },
                    { key: 'communityMessages', label: 'Community Messages', description: 'Notifications from community pods and discussions' },
                    { key: 'achievementAlerts', label: 'Achievement Alerts', description: 'Get notified when you earn new badges' },
                    { key: 'taskReminders', label: 'Task Reminders', description: 'Reminders for upcoming and overdue tasks' }
                  ].map((setting, index) => (
                    <div 
                      key={setting.key}
                      className="flex items-center justify-between p-4 bg-startup-slate rounded-xl animate-fade-in"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div>
                        <h3 className="font-medium text-startup-navy">{setting.label}</h3>
                        <p className="text-startup-gray text-sm">{setting.description}</p>
                      </div>
                      <button
                        onClick={() => handleNotificationToggle(setting.key as keyof typeof notifications)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          notifications[setting.key as keyof typeof notifications]
                            ? 'bg-startup-teal'
                            : 'bg-startup-gray/30'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            notifications[setting.key as keyof typeof notifications]
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subscription */}
            {activeTab === 'subscription' && (
              <div className="space-y-6">
                <div className="card-startup animate-fade-in">
                  <h2 className="text-2xl font-semibold text-startup-navy mb-6 flex items-center">
                    <CreditCard className="w-6 h-6 mr-3 text-startup-teal" />
                    Subscription Details
                  </h2>

                  <div className="bg-gradient-startup text-white rounded-xl p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold">{subscription.plan} Plan</h3>
                        <p className="text-white/80">Full access to all features</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">{subscription.amount}</div>
                        <div className="text-white/80 text-sm">Billed monthly</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        subscription.status === 'Active' ? 'bg-green-500/20 text-green-100' : 'bg-red-500/20 text-red-100'
                      }`}>
                        {subscription.status}
                      </span>
                      <span className="text-white/80 text-sm">Next billing: {subscription.nextBilling}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button className="btn-startup-outline">
                      Change Plan
                    </button>
                    <button className="btn-startup-outline">
                      Billing History
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Achievements */}
            {activeTab === 'achievements' && (
              <div className="card-startup animate-fade-in">
                <h2 className="text-2xl font-semibold text-startup-navy mb-6 flex items-center">
                  <Award className="w-6 h-6 mr-3 text-startup-teal" />
                  Your Achievements
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {achievements.map((achievement, index) => (
                    <div 
                      key={achievement.id}
                      className={`p-4 rounded-xl border-2 transition-all duration-300 animate-fade-in ${
                        achievement.earned
                          ? 'border-startup-gold bg-startup-gold/5 shadow-lg'
                          : 'border-startup-gray/20 bg-startup-slate'
                      }`}
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="text-center mb-3">
                        <div className={`text-4xl mb-2 ${achievement.earned ? 'animate-pulse-gold' : 'grayscale'}`}>
                          {achievement.icon}
                        </div>
                        <h3 className={`font-semibold ${achievement.earned ? 'text-startup-gold' : 'text-startup-gray'}`}>
                          {achievement.title}
                        </h3>
                      </div>
                      
                      <p className="text-startup-gray text-sm text-center mb-3">
                        {achievement.description}
                      </p>

                      {achievement.earned ? (
                        <div className="text-center">
                          <span className="bg-startup-gold/10 text-startup-gold px-3 py-1 rounded-full text-xs font-medium">
                            Earned
                          </span>
                        </div>
                      ) : (
                        <div className="text-center">
                          <span className="text-startup-gray text-xs">Not earned yet</span>
                        </div>
                      )}
                      
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 mt-1">
                          {achievement.earned 
                            ? `Earned recently`
                            : `${achievement.progress}/${achievement.maxProgress} progress`
                          }
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Privacy */}
            {activeTab === 'privacy' && (
              <div className="card-startup animate-fade-in">
                <h2 className="text-2xl font-semibold text-startup-navy mb-6 flex items-center">
                  <Shield className="w-6 h-6 mr-3 text-startup-teal" />
                  Privacy & Security
                </h2>

                <div className="space-y-6">
                  <div className="p-4 bg-startup-slate rounded-xl">
                    <h3 className="font-semibold text-startup-navy mb-2">Data Privacy</h3>
                    <p className="text-startup-gray text-sm mb-4">
                      Your data is encrypted and stored securely. We never share your personal information with third parties.
                    </p>
                    <button className="btn-startup-outline text-sm">
                      View Privacy Policy
                    </button>
                  </div>

                  <div className="p-4 bg-startup-slate rounded-xl">
                    <h3 className="font-semibold text-startup-navy mb-2">Account Security</h3>
                    <p className="text-startup-gray text-sm mb-4">
                      Keep your account secure with two-factor authentication and regular password updates.
                    </p>
                    <div className="space-y-2">
                      <button className="btn-startup-outline text-sm mr-2">
                        Change Password
                      </button>
                      <button className="btn-startup-outline text-sm">
                        Enable 2FA
                      </button>
                    </div>
                  </div>

                  <div className="p-4 bg-startup-red/5 border border-startup-red/20 rounded-xl">
                    <h3 className="font-semibold text-startup-red mb-2">Danger Zone</h3>
                    <p className="text-startup-gray text-sm mb-4">
                      These actions are permanent and cannot be undone.
                    </p>
                    <button className="bg-startup-red text-white px-4 py-2 rounded-lg text-sm hover:bg-startup-red/90 transition-colors">
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
